import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  FiUser, 
  FiMail, 
  FiLock, 
  FiCamera,
  FiSave,
  FiEdit2,
  FiCheckCircle,
  FiXCircle,
  FiShield,
  FiClock,
  FiAward,
  FiArrowDown,
  FiShoppingBag
} from 'react-icons/fi';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { getProfile } from '../store/slices/authSlice';

const profileSchema = yup.object({
  username: yup.string()
    .min(3, 'Username must be at least 3 characters')
    .max(20, 'Username must be less than 20 characters')
    .required('Username is required'),
  email: yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  currentPassword: yup.string()
    .when('$isChangingPassword', {
      is: true,
      then: (schema) => schema.required('Current password is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  newPassword: yup.string()
    .when('$isChangingPassword', {
      is: true,
      then: (schema) => schema
        .min(6, 'Password must be at least 6 characters')
        .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Must contain at least one number')
        .required('New password is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  confirmPassword: yup.string()
    .when('$isChangingPassword', {
      is: true,
      then: (schema) => schema
        .oneOf([yup.ref('newPassword')], 'Passwords must match')
        .required('Please confirm your password'),
      otherwise: (schema) => schema.notRequired()
    })
});

export const ProfilePage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    resolver: yupResolver(profileSchema),
    context: { isChangingPassword },
    defaultValues: {
      username: user?.username || '',
      email: user?.email || '',
    }
  });

  const watchNewPassword = watch('newPassword', '');

  useEffect(() => {
    // Refresh user data
    dispatch(getProfile());
  }, [dispatch]);

  useEffect(() => {
    // Reset form when user data changes
    reset({
      username: user?.username || '',
      email: user?.email || '',
    });
  }, [user, reset]);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size must be less than 2MB');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    try {
      // Here you would call your API to update profile
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      setIsChangingPassword(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const cancelEdit = () => {
    reset({
      username: user?.username || '',
      email: user?.email || '',
    });
    setIsEditing(false);
    setIsChangingPassword(false);
    setAvatarPreview(null);
    setAvatarFile(null);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile Information', icon: FiUser },
    { id: 'security', label: 'Security Settings', icon: FiShield },
    { id: 'activity', label: 'Recent Activity', icon: FiClock },
  ];

  const passwordStrength = () => {
    const password = watchNewPassword;
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const getStrengthColor = () => {
    const strength = passwordStrength();
    if (strength <= 25) return 'bg-red-500';
    if (strength <= 50) return 'bg-orange-500';
    if (strength <= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    const strength = passwordStrength();
    if (strength <= 25) return 'Weak';
    if (strength <= 50) return 'Fair';
    if (strength <= 75) return 'Good';
    return 'Strong';
  };

  // Sample activity data - replace with real data from your backend
  const recentActivity = [
    { action: 'Deposit', amount: 50000, status: 'success', date: '2024-01-15T10:30:00' },
    { action: 'Purchase', amount: 25000, status: 'success', date: '2024-01-14T15:45:00' },
    { action: 'Login', status: 'success', date: '2024-01-14T09:20:00' },
    { action: 'Profile Update', status: 'success', date: '2024-01-13T16:10:00' },
  ];

  return (
    <>
      <Helmet>
        <title>Profile | Nitro Logs</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-display font-bold text-luxury-900">
              My Profile
            </h1>
            <p className="text-luxury-600 mt-2">
              Manage your account settings and preferences
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {/* Sidebar */}
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="md:col-span-1"
            >
              {/* Profile Card */}
              <motion.div
                variants={fadeInUp}
                className="glass rounded-2xl p-4 sm:p-6 mb-6"
              >
                <div className="relative mb-4">
                  <div className="w-20 sm:w-24 h-20 sm:h-24 mx-auto rounded-2xl bg-gradient-to-r from-primary-500 to-secondary-500 p-1">
                    <div className="w-full h-full rounded-xl bg-white overflow-hidden">
                      {avatarPreview ? (
                        <img 
                          src={avatarPreview} 
                          alt="Avatar" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-secondary-100">
                          <FiUser className="w-6 sm:w-8 h-6 sm:h-8 text-primary-600" />
                        </div>
                      )}
                    </div>
                  </div>
                  {isEditing && (
                    <label className="absolute bottom-0 right-1/2 transform translate-x-8 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-700 transition-colors flex-shrink-0">
                      <FiCamera className="w-4 h-4 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-center text-luxury-900 truncate">
                  {user?.username}
                </h2>
                <p className="text-xs sm:text-sm text-center text-luxury-500 mb-4 truncate">
                  {user?.email}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold whitespace-nowrap">
                    {user?.role === 'admin' ? 'Administrator' : 'Member'}
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold whitespace-nowrap">
                    Verified
                  </span>
                </div>
              </motion.div>

              {/* Stats Card */}
              <motion.div
                variants={fadeInUp}
                className="glass rounded-2xl p-4 sm:p-6"
              >
                <h3 className="font-semibold text-luxury-900 mb-4 text-sm sm:text-base">Account Stats</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-luxury-600">Member Since</span>
                    <span className="font-medium text-luxury-900 text-right">
                      {user?.createdAt ? format(new Date(user.createdAt), 'MMM yyyy') : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-luxury-600">Total Orders</span>
                    <span className="font-medium text-luxury-900">0</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-luxury-600">Total Spent</span>
                    <span className="font-medium text-luxury-900">₦0</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-luxury-600">Wallet Balance</span>
                    <span className="font-bold text-primary-600">₦{user?.balance?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Main Content */}
            <motion.div
              variants={fadeInUp}
              className="md:col-span-2 lg:col-span-3 overflow-hidden"
            >
              {/* Tabs */}
              <div className="glass rounded-2xl p-1 mb-6 inline-flex w-full md:w-auto overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 px-4 md:px-6 py-3 rounded-xl transition-all duration-300 whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-luxury'
                          : 'text-luxury-600 hover:text-luxury-900'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="glass rounded-2xl p-6 sm:p-8 w-full"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                    <h2 className="text-xl font-semibold text-luxury-900">
                      Profile Information
                    </h2>
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors whitespace-nowrap"
                      >
                        <FiEdit2 className="w-4 h-4" />
                        <span>Edit Profile</span>
                      </button>
                    ) : (
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={cancelEdit}
                          className="px-4 py-2 border border-luxury-300 text-luxury-600 rounded-xl hover:bg-luxury-100 transition-colors flex-1 sm:flex-initial"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSubmit(onSubmit)}
                          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors flex-1 sm:flex-initial justify-center"
                        >
                          <FiSave className="w-4 h-4" />
                          <span>Save Changes</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <form className="space-y-6 w-full">
                    {/* Username Field */}
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Username
                      </label>
                      <div className="relative">
                        <FiUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                        <input
                          type="text"
                          {...register('username')}
                          disabled={!isEditing}
                          className={`input-luxury pl-12 w-full ${
                            !isEditing ? 'bg-luxury-100 cursor-not-allowed' : ''
                          } ${errors.username ? 'border-red-300' : ''}`}
                        />
                      </div>
                      {errors.username && (
                        <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                        <input
                          type="email"
                          {...register('email')}
                          disabled={!isEditing}
                          className={`input-luxury pl-12 w-full ${
                            !isEditing ? 'bg-luxury-100 cursor-not-allowed' : ''
                          } ${errors.email ? 'border-red-300' : ''}`}
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                      )}
                    </div>

                    {/* Email Verification Status */}
                    <div className="flex items-center space-x-2 p-4 bg-green-50 rounded-xl">
                      <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-green-700">
                        Your email is verified
                      </span>
                    </div>
                  </form>
                </motion.div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="glass rounded-2xl p-6 sm:p-8 w-full"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                    <h2 className="text-xl font-semibold text-luxury-900">
                      Security Settings
                    </h2>
                    {!isChangingPassword ? (
                      <button
                        onClick={() => setIsChangingPassword(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors whitespace-nowrap"
                      >
                        <FiLock className="w-4 h-4" />
                        <span>Change Password</span>
                      </button>
                    ) : (
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={cancelEdit}
                          className="px-4 py-2 border border-luxury-300 text-luxury-600 rounded-xl hover:bg-luxury-100 transition-colors flex-1 sm:flex-initial"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSubmit(onSubmit)}
                          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors flex-1 sm:flex-initial justify-center"
                        >
                          <FiSave className="w-4 h-4" />
                          <span>Update Password</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {isChangingPassword ? (
                    <form className="space-y-6 w-full">
                      {/* Current Password */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Current Password
                        </label>
                        <div className="relative">
                          <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                          <input
                            type="password"
                            {...register('currentPassword')}
                            className={`input-luxury pl-12 w-full ${errors.currentPassword ? 'border-red-300' : ''}`}
                            placeholder="Enter current password"
                          />
                        </div>
                        {errors.currentPassword && (
                          <p className="mt-1 text-sm text-red-600">{errors.currentPassword.message}</p>
                        )}
                      </div>

                      {/* New Password */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          New Password
                        </label>
                        <div className="relative">
                          <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                          <input
                            type="password"
                            {...register('newPassword')}
                            className={`input-luxury pl-12 w-full ${errors.newPassword ? 'border-red-300' : ''}`}
                            placeholder="Enter new password"
                          />
                        </div>
                        {errors.newPassword && (
                          <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
                        )}

                        {/* Password Strength Meter */}
                        {watchNewPassword && (
                          <div className="mt-3">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-luxury-500">Password Strength:</span>
                              <span className="text-xs font-medium" style={{ color: getStrengthColor().replace('bg-', 'text-') }}>
                                {getStrengthText()}
                              </span>
                            </div>
                            <div className="h-1.5 bg-luxury-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${getStrengthColor()} transition-all duration-300`}
                                style={{ width: `${passwordStrength()}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                          <input
                            type="password"
                            {...register('confirmPassword')}
                            className={`input-luxury pl-12 w-full ${errors.confirmPassword ? 'border-red-300' : ''}`}
                            placeholder="Confirm new password"
                          />
                        </div>
                        {errors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                        )}
                      </div>

                      {/* Password Requirements */}
                      <div className="p-4 bg-primary-50 rounded-xl">
                        <h4 className="text-sm font-semibold text-primary-700 mb-2">Password Requirements:</h4>
                        <ul className="space-y-1 text-sm text-primary-600">
                          <li className="flex items-center space-x-2">
                            <FiCheckCircle className={`w-4 h-4 flex-shrink-0 ${watchNewPassword?.length >= 6 ? 'text-green-500' : 'text-luxury-300'}`} />
                            <span>At least 6 characters</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <FiCheckCircle className={`w-4 h-4 flex-shrink-0 ${/[A-Z]/.test(watchNewPassword) ? 'text-green-500' : 'text-luxury-300'}`} />
                            <span>At least one uppercase letter</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <FiCheckCircle className={`w-4 h-4 flex-shrink-0 ${/[0-9]/.test(watchNewPassword) ? 'text-green-500' : 'text-luxury-300'}`} />
                            <span>At least one number</span>
                          </li>
                        </ul>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-6">
                      {/* Current Password Status */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-green-50 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <FiShield className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <div>
                            <h4 className="font-medium text-green-700">Password Status</h4>
                            <p className="text-sm text-green-600">Your password is secure and up to date</p>
                          </div>
                        </div>
                        <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      </div>

                      {/* Two-Factor Authentication */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-luxury-100 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <FiAward className="w-5 h-5 text-luxury-600 flex-shrink-0" />
                          <div>
                            <h4 className="font-medium text-luxury-700">Two-Factor Authentication</h4>
                            <p className="text-sm text-luxury-500">Add an extra layer of security to your account</p>
                          </div>
                        </div>
                        <button className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm hover:bg-primary-700 transition-colors whitespace-nowrap">
                          Enable
                        </button>
                      </div>

                      {/* Recent Login Sessions */}
                      <div className="p-4 bg-luxury-100 rounded-xl">
                        <h4 className="font-medium text-luxury-700 mb-3">Recent Login Sessions</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-start sm:items-center text-sm gap-2">
                            <span className="text-luxury-600">Chrome • Windows</span>
                            <span className="text-luxury-500 whitespace-nowrap">2 hours ago</span>
                          </div>
                          <div className="flex justify-between items-start sm:items-center text-sm gap-2">
                            <span className="text-luxury-600">Safari • iPhone</span>
                            <span className="text-luxury-500 whitespace-nowrap">Yesterday</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="glass rounded-2xl p-6 sm:p-8 w-full"
                >
                  <h2 className="text-xl font-semibold text-luxury-900 mb-6">
                    Recent Activity
                  </h2>

                  <div className="space-y-4">
                    {recentActivity.map((activity, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-luxury-100 rounded-xl hover:bg-luxury-200 transition-colors"
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            activity.action === 'Deposit' ? 'bg-green-100 text-green-600' :
                            activity.action === 'Purchase' ? 'bg-blue-100 text-blue-600' :
                            'bg-luxury-200 text-luxury-600'
                          }`}>
                            {activity.action === 'Deposit' ? <FiArrowDown /> :
                             activity.action === 'Purchase' ? <FiShoppingBag /> :
                             <FiUser />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-luxury-900 truncate">
                              {activity.action}
                              {activity.amount && ` • ₦${activity.amount.toLocaleString()}`}
                            </p>
                            <p className="text-xs text-luxury-500">
                              {format(new Date(activity.date), 'MMM d, yyyy • h:mm a')}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                          activity.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {activity.status}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};